import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import * as React from 'react';
import { authClient } from '@/lib/auth-client';
import { api } from '@/lib/api-client';
import { AuthLayout } from '@/components/auth/auth-layout';
import { PasswordInput } from '@/components/auth/password-input';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/login')({
  beforeLoad: async () => {
    const session = await authClient.getSession();
    if (session.data) {
      // Check if user is deactivated before redirecting to dashboard
      try {
        const result = await api.get<{
          data: { deactivated?: boolean };
        }>('/users/me/onboarding');
        if (result.data?.deactivated) {
          throw redirect({ to: '/deactivated' });
        }
      } catch (e) {
        if (e && typeof e === 'object' && 'to' in e) throw e;
      }
      throw redirect({ to: '/dashboard' });
    }
  },
  component: LoginPage,
});

const loginSchema = z.object({
  email: z.string().min(1, 'required').email('invalidEmail'),
  password: z.string().min(1, 'required'),
});

type LoginForm = z.infer<typeof loginSchema>;

function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [serverError, setServerError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    mode: 'onBlur',
    defaultValues: { email: '', password: '' },
  });

  const passwordValue = watch('password');

  const onSubmit = async (data: LoginForm) => {
    setServerError(null);
    setIsSubmitting(true);
    try {
      const result = await authClient.signIn.email({
        email: data.email,
        password: data.password,
      });

      if (result.error) {
        // Anti-enumeration: generic error
        setServerError(t('auth.loginGenericError'));
        return;
      }

      // Ensure activeOrganizationId is set in the session.
      // New sessions after sign-in don't have it — we must restore it.
      const freshSession = await authClient.getSession();
      if (!freshSession.data?.session?.activeOrganizationId) {
        try {
          const orgs = await authClient.organization.list();
          if (orgs.data && orgs.data.length > 0) {
            await authClient.organization.setActive({ organizationId: orgs.data[0].id });
          }
        } catch {
          // If org setup fails, onboarding will handle it
        }
      }

      // Check onboarding status to decide redirect
      try {
        const onboarding = await api.get<{
          data: { profileCompleted: boolean; workspaceCompleted: boolean; deactivated?: boolean };
        }>('/users/me/onboarding');
        if (onboarding.data?.deactivated) {
          await navigate({ to: '/deactivated' });
          return;
        }
        if (!onboarding.data?.profileCompleted || !onboarding.data?.workspaceCompleted) {
          await navigate({ to: '/onboarding' });
          return;
        }
      } catch {
        // If onboarding check fails, proceed to dashboard
      }

      await navigate({ to: '/dashboard' });
    } catch {
      setServerError(t('auth.loginGenericError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Registration link hidden — invite-only mode (ROOT creates accounts)
  const footer = null;

  return (
    <AuthLayout
      title={t('auth.loginTitle')}
      description={t('auth.loginDescription')}
      footer={footer}
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium text-foreground">
              {t('auth.emailLabel')}
            </Label>
            <Input
              id="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder={t('auth.emailPlaceholder')}
              className="h-12 rounded-lg bg-background text-sm"
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'email-error' : undefined}
              {...register('email')}
            />
            {errors.email && (
              <p id="email-error" className="text-xs text-destructive" role="alert">
                {t(`auth.validation.${errors.email.message}`)}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-sm font-medium text-foreground">
                {t('auth.passwordLabel')}
              </Label>
              <Link
                to="/forgot-password"
                className="text-xs text-muted-foreground underline-offset-4 hover:text-primary hover:underline"
                tabIndex={-1}
              >
                {t('auth.forgotPasswordLink')}
              </Link>
            </div>
            <PasswordInput
              id="password"
              autoComplete="current-password"
              placeholder={t('auth.passwordLoginPlaceholder')}
              className="h-12 rounded-lg bg-background text-sm"
              aria-invalid={!!errors.password}
              aria-describedby={errors.password ? 'password-error' : undefined}
              value={passwordValue}
              {...register('password')}
            />
            {errors.password && (
              <p id="password-error" className="text-xs text-destructive" role="alert">
                {t(`auth.validation.${errors.password.message}`)}
              </p>
            )}
          </div>
        </div>

        {serverError && (
          <div
            className="mt-5 rounded-lg border border-destructive/30 bg-destructive/5 p-3"
            role="alert"
            aria-live="assertive"
          >
            <p className="text-sm text-destructive">{serverError}</p>
          </div>
        )}

        <Button
          type="submit"
          className="mt-8 h-12 w-full cursor-pointer rounded-lg text-sm font-semibold"
          size="lg"
          disabled={isSubmitting}
          aria-disabled={isSubmitting}
          aria-busy={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              {t('auth.loginSubmitting')}
            </>
          ) : (
            t('auth.loginSubmit')
          )}
        </Button>
      </form>
    </AuthLayout>
  );
}
